import React, { useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useNotification } from './contexts/NotificationContext';
import { useSocket } from './contexts/SocketContext';
import { DateTime } from 'luxon';
import Select from 'react-select';

// Notification types that are ALERTS (something went wrong / needs a hand),
// shown with a warning mark instead of the read tick.
const ALERT_TYPES = new Set(['payment_review', 'money_critical', 'client_payment_not_completed', 'client_payment_in_review', 'wallet_drift', 'settlement_attention', 'odometer_jump', 'vehicle_alert']);

const Notifications = forwardRef((props, ref) => {
  const [allNotifications, setAllNotifications] = useState([]);
  const [visibleCount, setVisibleCount] = useState(6);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { unreadCount, refreshUnreadCount } = useNotification();
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const socket = useSocket();
  // value = the filter group the API understands (routes/notifications.js).
  const filterOptions = [
    { value: 'all', label: 'All Notifications' },
    { value: 'payments', label: 'Payments' },
    { value: 'lh_stain', label: 'LH Stain' },
  ];

  const fetchNotifications = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { type: filter };
      const res = await axios.get('/api/notifications', { params });
      setAllNotifications(res.data);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Unable to load notifications.');
    } finally {
      setLoading(false);
    }
  }, [filter]);


  const appendNotification = (newNotif) => {
    setAllNotifications((prev) => [newNotif, ...prev]);
  };

  useEffect(() => {
    setVisibleCount(6);
    fetchNotifications();
  }, [fetchNotifications]);


  useEffect(() => {
    if (!socket) return;

    const handleNewNotification = () => {
      fetchNotifications();
    };

    socket.on('new-notification', handleNewNotification);

    return () => {
      socket.off('new-notification', handleNewNotification);
    };
  }, [socket, fetchNotifications]);

  useImperativeHandle(ref, () => ({
    refreshNotifications: fetchNotifications,
    appendNotification,
  }));

  const markAsRead = async (id) => {
    try {
      await axios.patch(`/api/notifications/read`, { ids: [id] });
      setAllNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === id ? { ...n, is_read: true } : n
        )
      );
      await refreshUnreadCount();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.patch('/api/notifications/read/all');

      setAllNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true }))
      );

      await refreshUnreadCount();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const visibleNotifications = allNotifications.slice(0, visibleCount);

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <h2 className="notifications-title">
          Notifications
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount}</span>
          )}
        </h2>

        <div className="notifications-controls">
          <div style={{ minWidth: 180 }}>
            <Select
              inputId="notifications-filter"
              classNamePrefix="notif-filter"
              options={filterOptions}
              value={filterOptions.find(o => o.value === filter)}
              onChange={(opt) => setFilter(opt?.value ?? 'all')}
              isSearchable={false}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                container: (base) => ({ ...base, width: '100%' }),
              }}
            />
          </div>

          {allNotifications.some((n) => !n.is_read) && (
            <button className="mark-all-read" onClick={markAllAsRead}>
              Mark All Read
            </button>
          )}
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {visibleNotifications.length === 0 && !loading ? (
        <p className="no-notifications">You have no notifications.</p>
      ) : (
        <>
          <ul className="notification-list">
            {visibleNotifications.map((n) => {

              const targetUrl = n.target_url || '#';

              return (
                <li
                  key={n.recipient_notification_id}
                  className={`notification-item ${n.is_read ? 'read' : 'unread'}`}
                >
                  <div
                    className={`notification-status${ALERT_TYPES.has(n.related_entity_type) ? ' notification-status--alert' : ''}`}
                    onClick={() => !n.is_read && markAsRead(n.notification_id)}
                    title={ALERT_TYPES.has(n.related_entity_type) ? 'Needs attention' : (n.is_read ? 'Read' : 'Unread — click to mark as read')}
                  >
                    {/* A warning is a warning, read or not — the tick used to sit
                        on "Card payment not completed" and read as success
                        (owner 2026-09-23). Read/unread stays on the row style. */}
                    <FontAwesomeIcon icon={ALERT_TYPES.has(n.related_entity_type) ? faTriangleExclamation : (n.is_read ? faCheckCircle : faCircle)} />
                  </div>
                  <div
                    className="notification-content"
                    onClick={(e) => {
                      e.preventDefault();
                      if (!n.is_read) {
                        markAsRead(n.notification_id).then(() => {
                          refreshUnreadCount();
                          navigate(targetUrl);
                        });
                      } else {
                        navigate(targetUrl);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <h4>{n.title}</h4>
                    <p>{n.message}</p>
                    <span className="notification-date">
                      {DateTime.fromISO(n.created_at).setZone('Asia/Dubai').toFormat('dd LLL yyyy, HH:mm')}
                    </span>
                  </div>

                </li>
              );
            })}
          </ul>

          {visibleCount < allNotifications.length && (
            <button
              className="load-more-button"
              onClick={() => setVisibleCount((prev) => prev + 6)}
            >
              Load More
            </button>
          )}
        </>
      )}
    </div>
  );
});

export default Notifications;